// ============================================
// SEO HELPERS — breadcrumb + FAQ templates
// Keeps page components clean; change copy here once,
// applies everywhere.
// ============================================

const SITE = 'https://glitzandglambyjyoti.com';

// ── Breadcrumbs ──
// Each is an array of { name, url } that the SEO component turns into
// BreadcrumbList JSON-LD + shows as "Home › Shop › Earrings" in Google results.

export const homeCrumb = { name: 'Home', url: `${SITE}/` };

export const shopCrumbs = (category) => {
  const base = [homeCrumb, { name: 'Shop', url: `${SITE}/shop` }];
  if (!category || category === 'all') return base;
  const pretty = category.charAt(0).toUpperCase() + category.slice(1);
  return [...base, { name: pretty, url: `${SITE}/shop?category=${category}` }];
};

export const productCrumbs = (product) => {
  if (!product) return [];
  const crumbs = [
    homeCrumb,
    { name: 'Shop', url: `${SITE}/shop` },
  ];
  if (product.category) {
    const catPretty = product.category.charAt(0).toUpperCase() + product.category.slice(1);
    crumbs.push({ name: catPretty, url: `${SITE}/shop?category=${product.category}` });
  }
  crumbs.push({ name: product.name, url: `${SITE}/product/${product._id}` });
  return crumbs;
};

export const blogListCrumbs = () => [
  homeCrumb,
  { name: 'Blog', url: `${SITE}/blogs` },
];

export const blogDetailCrumbs = (blog) => {
  if (!blog) return blogListCrumbs();
  return [
    ...blogListCrumbs(),
    { name: blog.title, url: `${SITE}/blog/${blog._id}` },
  ];
};

export const policyCrumbs = (policy) => {
  if (!policy) return [homeCrumb];
  return [
    homeCrumb,
    { name: policy.title, url: `${SITE}/pages/${policy.slug || 'policy'}` },
  ];
};

// ── FAQ Templates ──
// Edit these — they appear as "People also ask" rich results in Google.

// Generic product FAQs — applies to every product page
// (Jyoti: replace these with your actual top 5 most-asked customer questions)
export const productFAQs = (product) => [
  {
    question: `Is the jewelry real gold or gold-plated?`,
    answer: `All our jewelry is premium artificial jewelry — gold-plated brass or alloy with high-quality finish. It's designed to look exactly like real gold at a fraction of the price, perfect for everyday wear and special occasions. Each piece is carefully hand-finished by our team.`,
  },
  {
    question: `How long does delivery take?`,
    answer: `We ship across India via Shiprocket. Metro cities (Delhi, Mumbai, Bangalore, Chennai, Hyderabad) typically receive orders in 3-5 working days. Other cities take 5-7 working days. Orders above ₹1000 get free shipping.`,
  },
  {
    question: `What if the product arrives damaged or I don't like it?`,
    answer: `We offer a 7-day easy return policy. If your product arrives damaged or doesn't match the description, contact us on WhatsApp within 7 days of delivery and we'll arrange a free pickup and full refund or replacement.`,
  },
  {
    question: `How do I care for artificial jewelry to make it last?`,
    answer: `Keep your jewelry away from water, perfume, and sweat. Store each piece in a dry, airtight pouch or box, ideally separated to prevent scratches. Wipe gently with a soft dry cloth after each use. With proper care, artificial jewelry can last for years.`,
  },
  {
    question: `Do you offer custom or bulk orders for weddings?`,
    answer: `Yes! We specialize in bridal jewelry sets and bulk orders for bridesmaids, mehendi functions, and wedding favors. Contact Jyoti directly on WhatsApp or Instagram @glitz_and_glam_by_jyoti for custom quotes and bridal consultation.`,
  },
];

// Shop page FAQs
export const shopFAQs = () => [
  {
    question: `What types of jewelry do you sell?`,
    answer: `We offer a complete range of artificial jewelry including earrings, necklaces, bangles, rings, maang tikkas, and complete bridal sets. We also stock hair accessories, sarees, and fashion items — handpicked by Jyoti for quality and style.`,
  },
  {
    question: `How do I choose the right size?`,
    answer: `Most of our jewelry is free-size or adjustable. For rings and bangles, we provide detailed size charts on each product page. When in doubt, message Jyoti on WhatsApp with your measurements and she'll help you pick the right size.`,
  },
  {
    question: `Do you have cash on delivery?`,
    answer: `Yes, COD is available for most pincodes in India, along with secure online payment options (UPI, cards, net banking, wallets) via Razorpay.`,
  },
];

// Blog list FAQs
export const blogFAQs = () => [
  {
    question: `How often is the Glitz & Glam blog updated?`,
    answer: `We publish new articles weekly on fashion tips, jewelry styling guides, festive outfit ideas, and trend updates from the Indian fashion scene.`,
  },
  {
    question: `Can I request a topic for a future blog post?`,
    answer: `Absolutely! Message us on Instagram @glitz_and_glam_by_jyoti or WhatsApp with your request — we love covering reader-suggested styling questions.`,
  },
];

// Organization schema — sitewide
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Store',
  '@id': `${SITE}/#organization`,
  name: 'Glitz & Glam by Jyoti',
  url: SITE,
  logo: `${SITE}/logo512.png`,
  image: `${SITE}/og-image.jpg`,
  description: 'Premium artificial jewelry and fashion accessories for women — handpicked by Jyoti, delivered across India.',
  sameAs: [
    'https://www.instagram.com/glitz_and_glam_by_jyoti',
  ],
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'IN',
  },
  priceRange: '₹₹',
  paymentAccepted: 'Cash, Credit Card, Debit Card, UPI, Net Banking, Wallets',
  currenciesAccepted: 'INR',
  areaServed: {
    '@type': 'Country',
    name: 'India',
  },
};
